import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button } from "@mui/material";
import { subWeeks, parseJSON, isBefore } from "date-fns";
import AddIcon from "@icons/myicons/plus-circled.svg?react";
import { Investment } from "@bn/api/apiv1";
import {
  getValueByInvestmentAndBookingType,
  toFirstTwoSignificantDecimal,
  toCurrency,
} from "@helpers";
import { useInvestmentLink } from "@hooks";
import {
  GainLossPercents,
  InvestmentInfo,
  Note,
  TableConfig,
} from "@components";
import { InvestmentActionMenu } from "./InvestmentActionMenu";

const QUOTE_PROVIDER_NONE = "none";

const checkIsShowModal = (investment: Investment) => {
  const { quote, quoteProvider } = investment;
  const showManualQuoteOptionAfter = subWeeks(new Date(), 2);
  if (quoteProvider === QUOTE_PROVIDER_NONE) return true;
  if (
    !quote?.lastDateTime ||
    isBefore(parseJSON(quote.lastDateTime), showManualQuoteOptionAfter)
  )
    return true;
  return false;
};

export type InvestmentTableAdditionalProps = {
  formatCurrency: (value: number, currency?: string) => string;
  images: Record<string, string | null | undefined>;
  notesByInvestmentId: Record<number, string | undefined>;
  onClickAddQuoteModal: (investment: Investment) => void;
  onAddBookingInvestment: (investment: Investment) => void;
  onEditInvestment: (investment: Investment) => void;
  onDeleteInvestment: (investment: Investment) => void;
  depotId?: number;
};

type RendererProps = {
  row: Investment;
  tableKey: string;
  isChildRow?: boolean;
} & Partial<InvestmentTableAdditionalProps>;

const NameCell = ({
  row,
  images = {},
  notesByInvestmentId,
  depotId,
}: RendererProps) => {
  const getInvestmentUrl = useInvestmentLink(depotId);
  const image = images[row.isin || ""] || images[row?.tickerSymbol || ""];

  return (
    <>
      <InvestmentInfo item={row} image={image} href={getInvestmentUrl(row)} />
      <Note comment={notesByInvestmentId?.[row.id]} />
    </>
  );
};

const QuoteCell = ({ row, onClickAddQuoteModal }: RendererProps) => {
  const { t } = useTranslation();

  return (
    <Box display="inline-flex" alignItems="center" gap={1}>
      <Typography variant="body4" noWrap>
        {getValueByInvestmentAndBookingType({
          value: row.quote?.last ?? 0,
          investmentType: row.type,
          currency: row.currency,
          percentageRange: "0-100",
        })}{" "}
      </Typography>
      {checkIsShowModal(row) && (
        <Button
          variant="tertiary"
          size="small"
          startIcon={<AddIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onClickAddQuoteModal?.(row);
          }}
          sx={{ marginLeft: 2 }}
        >
          {t("domains.investments.investmentTable.addQuote.quote")}
        </Button>
      )}
      {Boolean(row.quote?.changePercentAbs) && (
        <Box ml={2}>
          <GainLossPercents value={row.quote?.changePercentAbs ?? 0} />
        </Box>
      )}
    </Box>
  );
};

const PurchaseCell = ({ row, formatCurrency = toCurrency }: RendererProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="body4">
        {row.purchaseValue
          ? formatCurrency(row.purchaseValue, row.currency)
          : ""}
      </Typography>
      <Typography variant="caption2" color="textSecondary">
        {toFirstTwoSignificantDecimal(row.numberOfLots)}{" "}
        {t("domains.investments.investmentTable.pieces")}{" "}
        {getValueByInvestmentAndBookingType({
          value: row.purchasePrice ?? 0,
          investmentType: row.type,
          currency: row.currency,
        })}
      </Typography>
    </>
  );
};

const ChangeCell = ({ row, formatCurrency = toCurrency }: RendererProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
      >
        <Typography variant="body4">
          {row.currentMarketValue
            ? formatCurrency(row.currentMarketValue, row.currency)
            : ""}
        </Typography>
        <Box ml={2}>
          <GainLossPercents value={row.returnOnInvestmentInPercent!} />
        </Box>
      </Box>
      {Boolean(row.receivedTotalDividend) && (
        <Typography variant="caption2" color="textSecondary" fontWeight={600}>
          {row.receivedTotalDividend
            ? formatCurrency(row.receivedTotalDividend, row.currency)
            : ""}
          {t("domains.investments.investmentTable.dividends")}
        </Typography>
      )}
    </>
  );
};

const ActionsCell = ({
  row,
  onAddBookingInvestment,
  onEditInvestment,
  onDeleteInvestment,
}: RendererProps) => (
  <InvestmentActionMenu
    onAddBooking={() => onAddBookingInvestment?.(row)}
    onEdit={() => onEditInvestment?.(row)}
    onDelete={() => onDeleteInvestment?.(row)}
  />
);

type SortArgs = {
  sortField?: string;
  sortDirection?: TableSortDirection;
  onSort?: (field: string) => void;
};

export const useTableConfig = (
  onRowClick?: (investment: Investment) => void,
  { sortField, sortDirection, onSort }: SortArgs = {}
): TableConfig<Investment> => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      id: "investments-table",
      onRowClick,
      isRowClickable: () => Boolean(onRowClick),
      sorting:
        sortField && sortDirection && sortDirection !== "NONE"
          ? { field: sortField, direction: sortDirection }
          : { field: "name", direction: "ASC" as const },
      onSort,
      loadingRows: 3,
      loadingRowsHeight: "44px",
      getRowId: (data: Investment) => data.id,
      columns: [
        {
          id: "name",
          sortKey: "name",
          title: t("domains.investments.investmentTable.name"),
          sortable: true,
          renderer: NameCell,
        },
        {
          id: "quote",
          title: t("domains.investments.investmentTable.changePercentAbs"),
          sortable: true,
          sortKey: "quote.changePercentAbs",
          renderer: QuoteCell,
        },
        {
          id: "purchase",
          title: t("domains.investments.investmentTable.purchase"),
          sortable: true,
          sortKey: "purchaseValue",
          renderer: PurchaseCell,
        },
        {
          id: "change",
          title: t("domains.investments.investmentTable.change"),
          sortable: true,
          sortKey: "returnOnInvestmentInPercent",
          align: "right" as const,
          renderer: ChangeCell,
        },
        {
          id: "actions",
          title: t("investment.protocolTable.actions.title"),
          align: "right" as const,
          renderer: ActionsCell,
        },
      ],
    }),
    [t, onRowClick, sortField, sortDirection, onSort]
  );
};
